import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import { getPublicProfile } from '../api/api';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';


const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 180,
  height: 180,
  marginBottom: theme.spacing(1),
}));

const BoatAvatar = styled(Avatar)(({ theme }) => ({
  width: 180,
  height: 180,
  marginBottom: theme.spacing(1),
  borderRadius: theme.spacing(1),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ProfileView = () => {
  const { userId } = useParams();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getToken } = useKindeAuth();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [boatPictureUrl, setBoatPictureUrl] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) {
          throw new Error('Failed to retrieve token');
        }
        
        const profileData = await getPublicProfile(token, userId);
        setProfile(profileData);
        
        // Load profile picture if available
        if (profileData.profilePictureUrl) {
          try {
            const pictureResponse = await fetch(profileData.profilePictureUrl);
            const pictureBlob = await pictureResponse.blob();
            setProfilePictureUrl(URL.createObjectURL(pictureBlob));
          } catch (pictureError) {
            console.error('Error loading profile picture:', pictureError);
          }
        }
        
        // Load boat picture if available
        if (profileData.boatPictureUrl) {
          try {
            const boatPictureResponse = await fetch(profileData.boatPictureUrl);
            const boatPictureBlob = await boatPictureResponse.blob();
            setBoatPictureUrl(URL.createObjectURL(boatPictureBlob));
          } catch (boatPictureError) {
            console.error('Error loading boat picture:', boatPictureError);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile. Please try again later.');
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfile();
    }
  }, [userId, getToken]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography>{t('profile.notFound', 'Profile not found')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: 800,
          p: 2,
          borderRadius: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" gutterBottom>
          {profile.firstName} {profile.lastName}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          {profile.role === 'crew' ? t('profile.basicInfo.crew') : t('profile.basicInfo.skipper')}
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Profile Picture and About You Section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'flex-start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: isMobile ? 0 : 2, mb: isMobile ? 2 : 0 }}>
                <ProfileAvatar src={profilePictureUrl} alt={`${profile.firstName} ${profile.lastName}`} />
              </Box>
              <Box sx={{ flex: 1, alignSelf: isMobile ? 'flex-start' : 'auto', width: isMobile ? '100%' : 'auto' }}>
                <SectionTitle variant="h6">{t('profile.about', 'About')}</SectionTitle>
                <Typography variant="body1" paragraph>
                  {profile.aboutYou || t('profile.noInformation', 'No information provided')}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Boat Information Section (only for skippers) */}
          {profile.role === 'skipper' && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'flex-start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: isMobile ? 0 : 2, mb: isMobile ? 2 : 0 }}>
                  <BoatAvatar variant="rounded" src={boatPictureUrl} alt={profile.boatName || 'Boat'} />
                  {profile.boatName && (
                    <Typography variant="subtitle1" sx={{ mt: 1 }}>
                      {profile.boatName}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1, alignSelf: isMobile ? 'flex-start' : 'auto', width: isMobile ? '100%' : 'auto' }}>
                  <SectionTitle variant="h6">{t('profile.aboutTheBoat', 'About the boat')}</SectionTitle>
                  <Typography variant="body1" paragraph>
                    {profile.aboutBoat || t('profile.noInformation', 'No information provided')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default ProfileView;
